import { useState, useRef, FC, useEffect } from 'react';
import styled from 'styled-components';
import { CorporateTheme } from '../../utils/themes';
import BookMarkIcon from '../../assets/icons/BookMark';
import AdvanceSearchIcon from '../../assets/icons/AdvanceSearchIcon';
import { useTheme } from '../../middleware/ThemeContext';

const AdvanceSearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const AdvanceSearchButton = styled('button')<{ $isOpen?: boolean }>(
  ({ theme, $isOpen }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: CorporateTheme.spacing.sm,
    border: 'none',
    background: $isOpen
      ? theme.CorporateTheme.colors.filterBG
      : 'none',
    padding: `${CorporateTheme.spacing.sm} ${CorporateTheme.spacing.sm}`,
    cursor: 'pointer',
    color: theme.CorporateTheme.colors.textSecondary,
    fontSize: '14px',
    fontFamily: "'Raleway', sans-serif",
    fontWeight: 500,
    transition: 'all 0.2s',
    borderRadius: CorporateTheme.borderRadius,

    '&:hover': {
      color: theme.CorporateTheme.colors.accent,
      background: $isOpen
        ? theme.CorporateTheme.colors.filterBG
        : 'rgba(0, 0, 0, 0.05)',
    },
  })
);

const AdvanceSearchPanel = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  right: 0,
  background: theme.CorporateTheme.colors.background,
  border: `1px solid ${theme.CorporateTheme.colors.border}`,
  borderRadius: CorporateTheme.borderRadius,
  padding: CorporateTheme.spacing.sm,
  marginTop: CorporateTheme.spacing.xs,
  minWidth: '250px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  zIndex: 100,
}));

const ActionItem = styled('button')(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 500,
  fontFamily: "'Raleway', sans-serif",
  display: 'flex',
  alignItems: 'center',
  gap: CorporateTheme.spacing.sm,
  width: '100%',
  padding: CorporateTheme.spacing.sm,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  textAlign: 'left',
  color: theme.CorporateTheme.colors.textSecondary,
  transition: 'all 0.2s',

  '&:hover': {
    color: theme.CorporateTheme.colors.accent,
    background: theme.CorporateTheme.colors.filterBG,
  },

  '&:disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
}));

const AdvanceSearchDropDown = () => {
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <AdvanceSearchContainer ref={containerRef}>
      <AdvanceSearchButton $isOpen={isOpen} onClick={() => setIsOpen(!isOpen)} theme={theme}>
        <AdvanceSearchIcon color={theme.CorporateTheme.colors.textSecondary} />
        AdvanceSearch
      </AdvanceSearchButton>
      {isOpen && (
        <AdvanceSearchPanel theme={theme}>
          <ActionItem theme={theme}>
            <BookMarkIcon color={theme.CorporateTheme.colors.textSecondary} />
            Primary address based search
          </ActionItem>
          <ActionItem theme={theme}>
            <BookMarkIcon color={theme.CorporateTheme.colors.textSecondary} />
            Contact with facebook profile
          </ActionItem>
          <ActionItem theme={theme}>
            <BookMarkIcon color={theme.CorporateTheme.colors.textSecondary} />
            Contact based on region
          </ActionItem>
        </AdvanceSearchPanel>
      )}
    </AdvanceSearchContainer>
  );
};

export default AdvanceSearchDropDown;